// @flow strict

import * as React from 'react';
import useI18n from 'src/hooks/useI18n';

import {useSelector, useDispatch} from 'react-redux';

import {Chip} from '@spaced-out/ui-design-system/lib/components/Chip';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {hasCrmPermission, CRM_PERMISSIONS} from 'src/utils/crm-new/common';
import DefaultFieldsCard from 'src/components/crm/applicants/body/tabComponents/overview/default-fields-card.jsx';
import CandidateFieldsCard from 'src/components/crm/applicants/body/tabComponents/overview/candidate-fields-card.jsx';
import SkillsCard from 'src/components/crm/applicants/body/tabComponents/overview/skills-card/skills-card.jsx';

import {
  Card,
  CardHeader,
  CardTitle,
  CardActions,
  CardContent,
} from '@spaced-out/ui-design-system/lib/components/Card';

import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';

import {
  BodyMedium,
  BodySmall,
  BodyMediumBold,
  FormLabelSmall,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {getApplicantInformation} from 'src/action-creators/crm';
import {selectApplicantInformation} from 'src/selectors/crm';
import {useInflight} from 'src/hooks/redux';

import {
  Icon,
  SemanticIcon,
} from '@spaced-out/ui-design-system/lib/components/Icon';
import {EmptyState} from '@spaced-out/ui-design-system/lib/components/EmptyState';
import {getCandidateExperienceText} from 'src/utils/crm.js';
import SocialMediaProfile from 'src/components/crm/lib/social-media-profiles.jsx/social-media-profile.jsx';
import css from './candidate-details-card.css';
import classify from 'src/utils/classify';


type props = {
  candidateId: number,
};

const CandidateDetailsCard = ({candidateId}: props): React.Node => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const dispatch = useDispatch();
  const applicantDetails = useSelector(selectApplicantInformation);
  const [isLoading, setIsLoading] = React.useState(false);
  const isApplicantInformationLoading = useInflight(
    getApplicantInformation(candidateId),
  );
  const {resume} = applicantDetails ?? {};
  const {
    firstName,
    lastName,
    middleName,
    name: fullName,
    emails,
    phones,
    yearsOfExperience,
  } = resume ?? {};

  const initialCandidateDetails = {
    firstName,
    lastName,
    middleName,
    fullName,
    emails,
    phones,
    years: Math.floor(yearsOfExperience),
    months: Math.floor(yearsOfExperience * 12) % 12,
  };

  React.useEffect(() => {
    if (!applicantDetails || applicantDetails.id !== candidateId) {
      setIsLoading(true);
      if (candidateId) {
        dispatch(getApplicantInformation(candidateId)).finally(() =>
          setIsLoading(false),
        );
      }
    } else {
      setIsLoading(false);
    }
  }, [dispatch]);

  // Early return if no resume data
  if (!applicantDetails?.resume) {
    return (
      <Card>
        <CardContent>
          <BodyMedium>
            {t('No_resume_details_available', 'No resume details available')}
          </BodyMedium>
        </CardContent>
      </Card>
    );
  }

  if (isApplicantInformationLoading) {
    <CircularLoader className={css.loader} />;
  }

  return (
    <div className={css.candidateDetailsCardContainer}>
      <Card
        classNames={{
          wrapper: css.card,
        }}
      >
        <CardHeader>
          <CardTitle className={css.cardTitleContainer}>
            <SemanticIcon
              name="user"
              semantic="neutral"
              type="solid"
              size="small"
            />
            {t('Candidate_Details', 'Candidate Details')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className={css.candidateDetailsCardContentContainer}>
            <div>
              <BodySmall>{t('Full_Name', 'Full Name')}</BodySmall>
              <BodyMedium className={css.bold}>
                {applicantDetails.resume?.firstName +
                  ' ' +
                  applicantDetails.resume?.middleName +
                  ' ' +
                  applicantDetails.resume?.lastName || '-'}
              </BodyMedium>
            </div>
            <div>
              <BodySmall>{t('First_Name', 'First Name')}</BodySmall>
              <BodyMedium className={css.bold}>
                {applicantDetails.resume?.firstName || '-'}
              </BodyMedium>
            </div>
            <div>
              <BodySmall>{t('Middle_Name', 'Middle Name')}</BodySmall>
              <BodyMedium className={css.bold}>
                {applicantDetails.resume?.middleName || '-'}
              </BodyMedium>
            </div>
            <div>
              <BodySmall>{t('Last_Name', 'Last Name')}</BodySmall>
              <BodyMedium className={css.bold}>
                {applicantDetails.resume?.lastName || '-'}
              </BodyMedium>
            </div>
            <div className={css.candidateDetailBlock}>
              <BodySmall>{t('Gender', 'Gender')}</BodySmall>
              <BodyMedium className={css.bold}>
                {applicantDetails.resume?.gender || '-'}
              </BodyMedium>
            </div>
            <div className={css.candidateDetailBlock}>
              <BodySmall>{t('Email_ID', 'Email ID')}</BodySmall>
              <div className={css.candidateEmailsContainer}>
                {applicantDetails.resume?.emails?.map((email) => (
                  <Chip semantic="primary" size="medium" key={email}>
                    {email}
                  </Chip>
                ))}
              </div>
            </div>
            <div className={css.candidateDetailBlock}>
              <BodySmall>{t('Phone', 'Phone')}</BodySmall>
              <div className={css.candidateEmailsContainer}>
                {applicantDetails.resume?.phones?.map((email) => (
                  <Chip semantic="primary" size="medium" key={email}>
                    {email}
                  </Chip>
                ))}
              </div>
            </div>
            <div className={css.candidateDetailBlock}>
              <BodySmall>{t('Total_Experience', 'Total Experience')}</BodySmall>
              <BodyMedium className={css.bold}>
                {getCandidateExperienceText(
                  applicantDetails.resume?.yearsOfExperience ?? 0,
                )}
              </BodyMedium>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card
        classNames={{
          wrapper: css.card,
        }}
      >
        <CardHeader>
          <CardTitle className={css.cardTitleContainer}>
            <SemanticIcon
              name="briefcase"
              semantic="neutral"
              type="solid"
              size="small"
            />
            {t('Work_Experience', 'Work Experience')}
          </CardTitle>
        </CardHeader>
        <CardContent className={css.educationCardWrapper}>
          {applicantDetails.resume?.experiences?.length > 0 ? (
            [...(applicantDetails.resume?.experiences || [])]
              .sort((a, b) => {
                if (a.isCurrent && !b.isCurrent) {
                  return -1;
                }
                if (!a.isCurrent && b.isCurrent) {
                  return 1;
                }

                const aEndYear = a.isCurrent
                  ? new Date().getFullYear()
                  : a.endYear || -1;
                const bEndYear = b.isCurrent
                  ? new Date().getFullYear()
                  : b.endYear || -1;

                return bEndYear - aEndYear;
              })
              .map((experience, index) => (
                <div
                  key={experience.experienceOrg}
                  // className={css.experienceContainer}
                  className={classify(css.experienceContainer, {
                    [css.noSeprator]:
                      applicantDetails.resume?.experiences?.length - 1 ===
                      index,
                  })}
                >
                  <BodyMediumBold>{experience.title}</BodyMediumBold>
                  <BodySmall>
                    <Icon name="building" color="secondary" />
                    {experience.experienceOrg || '-'}
                  </BodySmall>
                  <BodySmall>
                    <Icon name="location-dot" color="secondary" />
                    {experience.location || '-'}
                  </BodySmall>
                  <BodySmall>
                    <Icon name="clock" color="secondary" />
                    {formatDateRange(
                      experience.startYear,
                      experience.startMonth,
                      experience.endYear,
                      experience.endMonth,
                      experience.isCurrent,
                    )}{' '}
                    •{' '}
                    {calculateExperienceDuration(
                      experience.startYear,
                      experience.startMonth,
                      experience.endYear,
                      experience.endMonth,
                      experience.isCurrent,
                    )}
                  </BodySmall>
                </div>
              ))
          ) : (
            <CandidateFieldsEmptyState
              title={t('No_Work_Experience', 'No Work Experience')}
              description={t(
                'No_work_experience_details_available',
                'No work experience details available',
              )}
              image="data"
            />
          )}
        </CardContent>
      </Card>

      <Card
        classNames={{
          wrapper: css.card,
        }}
      >
        <CardHeader>
          <CardTitle className={css.cardTitleContainer}>
            <SemanticIcon
              name="user-graduate"
              semantic="neutral"
              type="solid"
              size="small"
            />
            {t('Education', 'Education')}
          </CardTitle>
        </CardHeader>
        <CardContent className={css.educationCardWrapper}>
          {applicantDetails.resume?.educations?.length > 0 ? (
            applicantDetails.resume?.educations?.map((education, index) => (
              <div
                key={education.educationOrg}
                className={classify(css.educationContainer, {
                  [css.noSeprator]:
                    applicantDetails.resume?.educations?.length - 1 === index,
                })}
              >
                <BodyMediumBold>{education.degree}</BodyMediumBold>
                <BodySmall>
                  <Icon name="building" color="secondary" />
                  {education.educationOrg || '-'}
                </BodySmall>
                <BodySmall>
                  <Icon name="location-dot" color="secondary" />
                  {education.location || '-'}
                </BodySmall>
                <BodySmall>
                  <Icon name="clock" color="secondary" />
                  {education.startYear > 0 ? education.startYear : '-'} -{' '}
                  {education.endYear > 0 ? education.endYear : '-'}
                </BodySmall>
              </div>
            ))
          ) : (
            <CandidateFieldsEmptyState
              title={t('No_Education_Details', 'No Education Details')}
              description={t(
                'No_educational_background_available',
                'No educational background available',
              )}
              image="data"
            />
          )}
        </CardContent>
      </Card>

      <SkillsCard />

      <SocialMediaProfile applicantDetails={applicantDetails} />

      {hasCrmPermission(CRM_PERMISSIONS.VIEW_CANDIDATE_DEFAULT_FIELD) && (
        <DefaultFieldsCard showEditButton={false} />
      )}
      {hasCrmPermission(CRM_PERMISSIONS.VIEW_CANDIDATE_CUSTOM_FIELD) && (
        <CandidateFieldsCard showEditButton={false} />
      )}
    </div>
  );
};

export default CandidateDetailsCard;

export const formatDateRange = (
  startYear: number,
  startMonth: number,
  endYear: number,
  endMonth: number,
  isCurrent: boolean,
  // eslint-disable-next-line max-params
): string => {
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const formatDate = (year, month) => {
    if (!year || year <= 0) {
      return '';
    }

    if (month >= 1 && month <= 12) {
      return `${monthNames[month - 1]} ${year}`;
    }

    return `${year}`;
  };

  if (!startYear || startYear <= 0) {
    return '-';
  }

  const formattedStartDate = formatDate(startYear, startMonth);

  if (isCurrent || endYear <= 0) {
    return `${formattedStartDate} - Current`;
  }

  const formattedEndDate = formatDate(endYear, endMonth);

  if (formattedEndDate) {
    return `${formattedStartDate} - ${formattedEndDate}`;
  }

  return formattedStartDate;
};

export const calculateExperienceDuration = (
  startYear: number,
  startMonth: number,
  endYear: number,
  endMonth: number,
  isCurrent: boolean = false,
  // eslint-disable-next-line max-params
): string => {
  if (!startYear || startYear <= 0) {
    return '';
  }

  const now = new Date();
  const end =
    isCurrent || !endYear || endYear <= 0
      ? now
      : new Date(endYear, endMonth > 0 ? endMonth - 1 : 11, 1);

  const start = new Date(startYear, startMonth > 0 ? startMonth - 1 : 0, 1);

  let yearsDiff = end.getFullYear() - start.getFullYear();
  let monthsDiff = end.getMonth() - start.getMonth();

  if (monthsDiff < 0) {
    yearsDiff--;
    monthsDiff += 12;
  }
  const years = yearsDiff > 0 ? `${yearsDiff} yrs ` : '';
  const months = monthsDiff > 0 ? `${monthsDiff} month` : '';

  return `${years}${months}`.trim();
};

const CandidateFieldsEmptyState = ({description, title, image}) => {
  return (
    <EmptyState description={description} title={title} imageVariant={image} />
  );
};
